/**
 * WeaveIn.ai Platform - ECM Reminders API Service
 * TypeScript service layer for CalAIM ECM notification management:
 *   - Bulk acknowledge reminders
 *   - Member snooze/pause
 *   - Compliance dashboard
 *   - Organization reminder configuration
 */

import { apiClient } from '@/services/apiClient';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type ContactStatus = 'on_track' | 'due' | 'overdue' | 'critical' | 'completed';

export type SnoozeReason =
  | 'hospitalized'
  | 'unreachable'
  | 'vacation'
  | 'incarcerated'
  | 'member_request'
  | 'pending_transfer'
  | 'bereavement'
  | 'other';

// --- Bulk Acknowledge ---

export interface BulkAcknowledgeRequest {
  reminder_type?: string;
  enrollment_id?: number;
  reminder_ids?: number[];
}

export interface BulkAcknowledgeResponse {
  success: boolean;
  acknowledged_count: number;
  notifications_read_count: number;
  message: string;
}

// --- Snooze ---

export interface SnoozeCreateRequest {
  member_id: number;
  enrollment_id?: number;
  reason: SnoozeReason;
  reason_details?: string;
  snooze_days: number;
}

export interface Snooze {
  id: number;
  member_id: number;
  enrollment_id: number | null;
  reason: SnoozeReason;
  reason_details: string | null;
  snooze_until: string;
  is_active: boolean;
  snoozed_by: number;
  created_at: string;
}

export interface SnoozeListResponse {
  snoozes: Snooze[];
  total: number;
}

// --- Compliance Dashboard ---

export interface MemberContactStatus {
  enrollment_id: number;
  member_id: number;
  member_name: string;
  case_number: string | null;
  staff_user_id: number | null;
  staff_name: string | null;
  last_contact_date: string | null;
  contact_status: ContactStatus;
  days_remaining: number;
  highest_escalation_tier: number | null;
  is_snoozed: boolean;
  snooze_reason: string | null;
  snooze_until: string | null;
  // Notification preference status (from migration 305)
  notifications_opted_out?: boolean;
  notifications_paused?: boolean;
  notification_paused_reason?: string | null;
  member_max_escalation_tier?: number | null;
}

export interface ComplianceSummary {
  total: number;
  completed: number;
  on_track: number;
  due: number;
  overdue: number;
  critical: number;
  snoozed: number;
}

export interface ComplianceDashboardResponse {
  members: MemberContactStatus[];
  summary: ComplianceSummary;
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface ComplianceDashboardParams {
  program_id?: number;
  staff_user_id?: number;
  contact_status?: ContactStatus;
  include_snoozed?: boolean;
  page?: number;
  page_size?: number;
}

// --- Member Notification Preferences ---

export interface MemberNotificationPreferences {
  member_id: number;
  cbo_id: number;
  sms_enabled: boolean;
  email_enabled: boolean;
  in_app_enabled: boolean;
  push_enabled: boolean;
  quiet_start: string | null;
  quiet_end: string | null;
  timezone: string;
  max_reminders_per_day: number | null;
  max_reminders_per_week: number | null;
  opted_out: boolean;
  notification_paused: boolean;
  paused_reason: string | null;
  max_escalation_tier: number | null; // null=program default, 0=disabled, 1-4=cap
  updated_at: string | null;
}

export interface MemberNotificationPreferencesUpdate {
  sms_enabled?: boolean;
  email_enabled?: boolean;
  in_app_enabled?: boolean;
  push_enabled?: boolean;
  quiet_start?: string;
  quiet_end?: string;
  timezone?: string;
  max_reminders_per_day?: number;
  max_reminders_per_week?: number;
  opted_out?: boolean;
  notification_paused?: boolean;
  paused_reason?: string;
  max_escalation_tier?: number;
}

export interface BulkNotificationPreferencesUpdate {
  member_ids?: number[];
  population_of_focus?: string;
  staff_user_id?: number;
  enrollment_status?: string;
  notification_paused?: boolean;
  paused_reason?: string;
  opted_out?: boolean;
  max_escalation_tier?: number;
  sms_enabled?: boolean;
  email_enabled?: boolean;
  max_reminders_per_day?: number;
}

export interface BulkPreferencesResponse {
  success: boolean;
  updated_count: number;
  cancelled_reminders_count: number;
  message: string;
}

// --- Program-Level Reminder Config ---

export interface ProgramReminderConfig {
  cbo_id: number;
  program_id: number;
  escalation_interval_hours: number;
  max_escalation_tier: number;
  monthly_contact_start_day: number;
  monthly_contact_warn_day: number;
  monthly_contact_urgent_day: number;
  reassessment_window_days: number;
  default_channels: string[];
  updated_at: string | null;
}

export interface ProgramReminderConfigUpdate {
  escalation_interval_hours?: number;
  max_escalation_tier?: number;
  monthly_contact_start_day?: number;
  monthly_contact_warn_day?: number;
  monthly_contact_urgent_day?: number;
  reassessment_window_days?: number;
  default_channels?: string[];
}

// ============================================================================
// API CLIENT METHODS
// ============================================================================

export const ecmRemindersAPI = {
  // Feature 1: Bulk Acknowledge
  bulkAcknowledge: async (data: BulkAcknowledgeRequest): Promise<BulkAcknowledgeResponse> => {
    const response = await apiClient.patch<BulkAcknowledgeResponse>(
      '/api/ecm-reminders/bulk-acknowledge',
      data
    );
    return response.data;
  },

  // Feature 2: Snooze Management
  createSnooze: async (data: SnoozeCreateRequest): Promise<Snooze> => {
    const response = await apiClient.post<Snooze>('/api/ecm-reminders/snooze', data);
    return response.data;
  },

  cancelSnooze: async (snoozeId: number): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/api/ecm-reminders/snooze/${snoozeId}`);
    return response.data;
  },

  listSnoozes: async (params?: {
    member_id?: number;
    active_only?: boolean;
  }): Promise<SnoozeListResponse> => {
    const response = await apiClient.get<SnoozeListResponse>('/api/ecm-reminders/snoozes', {
      params,
    });
    return response.data;
  },

  // Feature 4: Compliance Dashboard
  getComplianceDashboard: async (
    params?: ComplianceDashboardParams
  ): Promise<ComplianceDashboardResponse> => {
    const response = await apiClient.get<ComplianceDashboardResponse>(
      '/api/ecm-reminders/compliance-dashboard',
      { params }
    );
    return response.data;
  },

  // Feature 5: Program-Level Reminder Config
  getReminderConfig: async (programId: number): Promise<ProgramReminderConfig> => {
    const response = await apiClient.get<ProgramReminderConfig>('/api/ecm-reminders/config', {
      params: { program_id: programId },
    });
    return response.data;
  },

  updateReminderConfig: async (
    programId: number,
    data: ProgramReminderConfigUpdate
  ): Promise<ProgramReminderConfig> => {
    const response = await apiClient.put<ProgramReminderConfig>('/api/ecm-reminders/config', data, {
      params: { program_id: programId },
    });
    return response.data;
  },

  // Feature 6: Member Notification Preferences
  getMemberPreferences: async (memberId: number): Promise<MemberNotificationPreferences> => {
    const response = await apiClient.get<MemberNotificationPreferences>(
      `/api/ecm-reminders/member-preferences/${memberId}`
    );
    return response.data;
  },

  updateMemberPreferences: async (
    memberId: number,
    data: MemberNotificationPreferencesUpdate
  ): Promise<MemberNotificationPreferences> => {
    const response = await apiClient.put<MemberNotificationPreferences>(
      `/api/ecm-reminders/member-preferences/${memberId}`,
      data
    );
    return response.data;
  },

  // Feature 7: Bulk/Cohort Notification Preferences
  bulkUpdatePreferences: async (
    data: BulkNotificationPreferencesUpdate
  ): Promise<BulkPreferencesResponse> => {
    const response = await apiClient.put<BulkPreferencesResponse>(
      '/api/ecm-reminders/bulk-preferences',
      data
    );
    return response.data;
  },
};

export default ecmRemindersAPI;
